import { useState, useEffect } from 'react';

interface RateCard {
  label: string;
  value: string;
  unit: string;
  badge: string;
  badgeColor: string;
  highlight: boolean;
}

export default function RateSection() {
  const [liveRate, setLiveRate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [updatedTime, setUpdatedTime] = useState('');

  useEffect(() => {
    fetch('https://api.exchangerate.host/latest?base=USD&symbols=VES')
      .then((res) => {
        if (!res.ok) throw new Error('Network error');
        return res.json();
      })
      .then((data) => {
        const rate = data?.rates?.VES;
        if (rate) {
          setLiveRate(Number(rate).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
          const now = new Date();
          setUpdatedTime(
            now.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' })
          );
        } else {
          setError(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  const rates: RateCard[] = [
    {
      label: 'BCV Oficial',
      value: '36.42',
      unit: 'Bs',
      badge: 'Oficial',
      badgeColor: 'bg-blue-50 text-blue-700 border border-blue-200',
      highlight: false,
    },
    {
      label: 'Paralelo / Mercado',
      value: loading ? '...' : error ? 'N/D' : (liveRate ?? '—'),
      unit: 'Bs',
      badge: 'Más usado',
      badgeColor: 'bg-green-50 text-green-700 border border-green-200',
      highlight: true,
    },
    {
      label: 'Monitor Dólar',
      value: '38.75',
      unit: 'Bs',
      badge: 'Referencia',
      badgeColor: 'bg-amber-50 text-amber-700 border border-amber-200',
      highlight: false,
    },
  ];

  return (
    <section id="tasa" className="py-20 md:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-green-600 text-xs font-bold uppercase tracking-widest mb-3">
            Tasas de cambio
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3 tracking-tight">
            Tasa del dólar hoy
          </h2>
          <p className="text-gray-500 text-base max-w-md mx-auto">
            Valores de referencia en bolívares por 1 dólar estadounidense.
          </p>
        </div>

        {/* Rate Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
          {rates.map((rate) => (
            <div
              key={rate.label}
              className={`relative rounded-2xl p-7 flex flex-col items-center text-center transition-all ${
                rate.highlight
                  ? 'bg-green-600 text-white ring-4 ring-green-200 scale-105'
                  : 'bg-gray-50 text-gray-900'
              }`}
            >
              {rate.highlight && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-green-400 text-white text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full whitespace-nowrap shadow">
                    ★ Más usado
                  </span>
                </div>
              )}
              <p className={`text-sm font-semibold mb-3 ${rate.highlight ? 'text-green-100' : 'text-gray-500'}`}>
                {rate.label}
              </p>
              {rate.highlight && (
                <p className="text-xs font-semibold text-green-200 uppercase tracking-wide mb-2">
                  Tasa del mercado (referencial)
                </p>
              )}
              <div className={`flex items-baseline gap-1 mb-1 ${rate.highlight ? 'text-white' : 'text-gray-900'}`}>
                <span className={`text-sm font-bold ${rate.highlight ? 'text-green-200' : 'text-gray-400'}`}>1 USD =</span>
              </div>

              {/* Live rate display */}
              {rate.highlight && loading ? (
                <div className="flex items-center gap-2 my-3">
                  <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block"></span>
                  <span className="text-green-100 text-sm font-medium">Cargando...</span>
                </div>
              ) : (
                <div className="flex items-end gap-2 mb-3">
                  <span className={`font-black tracking-tight ${
                    rate.highlight ? 'text-white' : 'text-gray-900'
                  } ${rate.value.length > 8 ? 'text-3xl' : 'text-5xl'}`}>
                    {rate.value}
                  </span>
                  <span className={`text-xl font-bold mb-1.5 ${rate.highlight ? 'text-green-200' : 'text-gray-400'}`}>
                    {rate.unit}
                  </span>
                </div>
              )}

              {!rate.highlight && (
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${rate.badgeColor}`}>
                  {rate.badge}
                </span>
              )}

              {rate.highlight && !loading && error && (
                <span className="text-xs text-green-200 mt-1">No se pudo cargar la tasa</span>
              )}

              {rate.highlight && (
                <p className="text-xs text-green-100/80 mt-3 leading-relaxed max-w-[220px] text-center">
                  Basado en referencias del mercado paralelo en Venezuela. La tasa puede cambiar constantemente a lo largo del día.
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Updated note */}
        <div className="flex items-center justify-center gap-2 text-gray-400 text-sm">
          <span className="w-2 h-2 rounded-full bg-green-500 inline-block animate-pulse"></span>
          <span>
            {loading
              ? 'Cargando tasa en tiempo real...'
              : error
              ? 'Actualizado diariamente · Datos referenciales'
              : `Tasa en tiempo real · Hoy a las ${updatedTime}`}
          </span>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-xs text-gray-400 mt-3">
          Los valores mostrados son referenciales y pueden variar según la fuente.
        </p>

        {/* CTA Link */}
        <div className="text-center mt-8">
          <a
            href="https://remesasve.com"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex items-center gap-2 text-green-600 font-semibold text-base hover:text-green-700 transition-colors cursor-pointer"
          >
            Antes de enviar dinero, compara las opciones para obtener la mejor tasa disponible hoy.
            <span className="text-xl">👉</span>
          </a>
        </div>
      </div>
    </section>
  );
}
